/* ============================================
   QUOTE DASHBOARD — Deal Feed Note-Entry Handlers
   Asset: dashboard-deal-feed.js

   Extracted from dashboard-main.js on 2026-05-21 as Phase A.4 of the
   dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js.

   This module owns the CRUD handlers for note entries on the Activity
   card / Deal Feed:
     - Save (handlePendingNoteSave, handleLeadNoteSave, handleNoteEntryAdd)
     - Edit (showEntryMutatePopover, enterNoteEditMode, exitNoteEditMode,
             handleNoteEntryEdit)
     - Delete (handleNoteEntryDelete)
     - Helpers (formatLeadNoteStamp — internal, not exposed)

   The Activity card's HTML BUILDER (inline inside buildDetailHtml) and
   the chip emoji/label maps remain in main.js for now — they'll move
   out in Phase B as part of the bigger buildDetailHtml extraction.
   This module just owns the side-effect handlers; the render layer
   stays with the rest of the row-detail markup.

   Public API (attached to window.RQD):
     handlePendingNoteSave(btn)
     handleLeadNoteSave(btn)
     handleNoteEntryAdd(btn)
     showEntryMutatePopover(anchorEl, editBtn, delBtn)
     enterNoteEditMode(btn)
     exitNoteEditMode(entryEl)
     handleNoteEntryEdit(btn)
     handleNoteEntryDelete(btn)

   Init contract (RQD.initDealFeed called once by main.js at boot):
     esc, formatNoteDate, formatFollowUpDate, daysFromNowYmd,
     fetchQuotes, getAllQuotes (closure-getter),
     armFollowUp, clearFollowUp, _syncSaveEnabled,
     API, Auth (defaulted from window.RepPortal if omitted)
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  /* Ryan 2026-09-12: the bold "Follow-up:" label, so this optimistic copy of an entry matches the
     persisted renderer's (dashboard-activity-card.js, same constant, same markup). Two spellings
     would put the label in one weight the instant you save and another after the next refresh. */
  var _FU_LABEL = '<span class="rqd-notes-log__entry-line-label">Follow-up:</span>';

  /* CUSTOM_CALLDETAIL_BY_CLAUDE (Ryan 2026-09-20) — WHY THERE WAS NO VOICEMAIL.
     ────────────────────────────────────────────────────────────────────────
     Optional colour on the "Outbound → No answer → Didn't leave VM" leaf only. Picking one is
     never required: the leaf already prefills its note and ungates Save, and that path is
     untouched.

     ⛔ THIS IS THE ONLY PLACE THE FIVE REASONS ARE SPELLED. The chip markup
     (dashboard-activity-card.js), the appended sentence (dashboard-main.js) and BOTH feed
     renderers read from here. The outcome strings above them are duplicated across two renderers
     with a standing warning that they must match; that warning is the reason this one is not.
     ⛔ It is also the allow-list. `label` is injected into feed HTML unescaped (fixed strings,
     like the outcome sentence beside it), so a `callDetail` the server sends that is NOT an id
     here resolves to '' and renders nothing.

     ⚠️ `id` is the wire value and it is mirrored in the worker's /note-entry-add enum
     (rfq-worker src/lib/validation.js). Adding a fifth reason means editing BOTH — the worker
     rejects an unknown id with a 400.

     chip     — the button, capped by the 132px composer column (~110px of text at 12px/600)
     label    — the tail of the feed's "Call details:" sentence
     sentence — appended after "Did not leave a voicemail." in the note */
  var CALL_DETAILS = [
    { id: 'mailbox-full', chip: 'Mailbox full',   label: 'mailbox full',      sentence: 'Mailbox was full.' },
    { id: 'no-vm-box',    chip: 'No VM set up',   label: 'no voicemail box',  sentence: 'The line has no voicemail box.' },
    { id: 'after-hours',  chip: 'After hours',    label: 'after hours',       sentence: 'Reached an after-hours recording.' },
    { id: 'cut-off',      chip: 'Cut off mid-VM', label: 'cut off partway',   sentence: 'The line dropped partway through.' },
    /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — the fifth: a number that is suspected or
       confirmed wrong, e.g. RW-1444-A's "(166) 133-3077". The inquiry follow-up and the first-quote
       email read it back to the customer and ask for the right one. */
    { id: 'wrong-number', chip: 'Wrong number',   label: 'suspected wrong number', sentence: 'Suspected wrong number.' }
  ];
  function callDetailBy(id) {
    var v = String(id || '');
    for (var i = 0; i < CALL_DETAILS.length; i++) if (CALL_DETAILS[i].id === v) return CALL_DETAILS[i];
    return null;
  }
  /* The tail the feed appends to "Outbound, no answer". Empty for anything unknown, and empty
     unless the call really was a no-answer with no voicemail — a reason cannot outlive the leaf
     it was picked under (an entry edited later, or a hand-written API call, could disagree). */
  function callDetailTail(e) {
    if (!e || String(e.callOutcome || '') !== 'no-answer' || String(e.callVm || '') !== 'none') return '';
    var d = callDetailBy(e.callDetail);
    return d ? ' — ' + d.label : '';
  }
  window.RQD.CALL_DETAILS = CALL_DETAILS;
  window.RQD.callDetailBy = callDetailBy;
  window.RQD.callDetailTail = callDetailTail;

  // Injected by RQD.initDealFeed (from main.js boot)
  var esc, formatNoteDate, formatFollowUpDate, daysFromNowYmd;
  var fetchQuotes, getAllQuotes;
  var armFollowUp, clearFollowUp, _syncSaveEnabled;
  var API, Auth;
  /* Ryan 2026-05-22 hotfix — Phase A.4 missed these three deps. The
     formatFollowUpRemaining + _followUpDayCount calls fire when an
     existing follow-up's date is edited (the "Follow-up increased/
     decreased. Was at X, set for Y" path inside the optimistic prepend).
     refreshProgressBar was silently no-op'd by a typeof guard but should
     have been wired. All three are function declarations in main.js so
     they're safe to pass directly per the init-deps hoisting trap memo. */
  var formatFollowUpRemaining, _followUpDayCount, refreshProgressBar;
  var repaintEntriesFor;   // CUSTOM_FOLLOWUPFIX_BY_CLAUDE - dashboard-main.js's surgical entries redraw

    async function handlePendingNoteSave(btn) {
      var ref = btn.dataset.ref;
      var textarea = document.querySelector('.rqd-pending-notes[data-ref="' + ref + '"]');
      if (!textarea) return;
      // Same auto-stamp pattern as handleLeadNoteSave — append "— {rep} · {ts}"
      // only when content actually changed since the last save (no spam stamps
      // when re-clicking Save). After save, sync defaultValue baseline so the
      // next edit appends a fresh stamp at the bottom.
      if (!('lastSaved' in textarea.dataset)) {
        textarea.dataset.lastSaved = textarea.defaultValue;
      }
      var current = textarea.value;
      var baseline = textarea.dataset.lastSaved;
      var noteToSave = current;
      // Mirror handleLeadNoteSave: stamp only when content changed AND
      // there's still real content to stamp. Clearing notes saves empty.
      if (current !== baseline && current.trim() !== '') {
        var sess = window.RepPortal && window.RepPortal.Auth.getSession();
        var who = (sess && sess.code) ? sess.code : 'rep';
        var stamp = formatLeadNoteStamp(new Date());
        noteToSave = current.replace(/\s+$/, '') + '\n— ' + who + ' · ' + stamp;
      } else if (current.trim() === '') {
        noteToSave = '';
      }

      btn.textContent = 'Saving...';
      btn.disabled = true;
      try {
        await API.post('/update-pending', { ref: ref, note: noteToSave });
        textarea.value = noteToSave;
        textarea.dataset.lastSaved = noteToSave;
        // Scroll the textarea to the bottom so the freshly-appended stamp
        // is visible without forcing the rep to scroll a tall log.
        textarea.scrollTop = textarea.scrollHeight;
        btn.textContent = 'Saved!';
        setTimeout(function() { btn.textContent = 'Save Note'; btn.disabled = false; }, 1500);
      } catch (err) {
        btn.textContent = 'Save Note';
        btn.disabled = false;
        alert('Failed: ' + (err.message || 'Unknown error'));
      }
    }

    // Save rep notes typed against a lead (NEW / SAMPLE / DEAD / ON HOLD).
    // Worker writes lead.notes + mirrors into workstation_meta.notes if a
    // record already exists, so the workstation sticky-notes sidebar stays
    // in sync. Notes auto-carry to workstation_meta on /create-quote-from-lead.
    //
    // Auto-stamp pattern: when the textarea differs from what was saved last,
    // we append "— {rep} · YYYY-MM-DD h:MMam" to the trailing content before
    // sending. No-op when content is unchanged (re-clicking Save shouldn't
    // pile up timestamps). Lightweight stepping stone toward a real CRM
    // activity log — when we promote to a structured {ts, by, text} array
    // server-side, these inline stamps parse cleanly into entries.
    function formatLeadNoteStamp(d) {
      var Y = d.getFullYear();
      var M = String(d.getMonth() + 1).padStart(2, '0');
      var D = String(d.getDate()).padStart(2, '0');
      var h = d.getHours();
      var ampm = h >= 12 ? 'pm' : 'am';
      h = h % 12; if (h === 0) h = 12;
      var m = String(d.getMinutes()).padStart(2, '0');
      return Y + '-' + M + '-' + D + ' ' + h + ':' + m + ampm;
    }
    async function handleLeadNoteSave(btn) {
      var ref = btn.dataset.ref;
      var textarea = document.querySelector('.rqd-lead-notes[data-ref="' + ref + '"]');
      if (!textarea) return;
      // Initialize the dirty-baseline once per textarea (defaultValue holds
      // the value rendered into the HTML, which is what we persisted last).
      if (!('lastSaved' in textarea.dataset)) {
        textarea.dataset.lastSaved = textarea.defaultValue;
      }
      var current = textarea.value;
      var baseline = textarea.dataset.lastSaved;
      var noteToSave = current;
      // Stamp only when content changed AND there's still real content to
      // stamp — clearing all notes saves an empty string verbatim instead of
      // leaving a phantom "— RW · {time}" line attached to nothing.
      if (current !== baseline && current.trim() !== '') {
        var sess = window.RepPortal && window.RepPortal.Auth.getSession();
        var who = (sess && sess.code) ? sess.code : 'rep';
        var stamp = formatLeadNoteStamp(new Date());
        // Trim trailing whitespace before appending so consecutive saves
        // don't leave double blanks before each stamp.
        noteToSave = current.replace(/\s+$/, '') + '\n— ' + who + ' · ' + stamp;
      } else if (current.trim() === '') {
        // Normalize whitespace-only saves to a clean empty string so the
        // baseline reset below doesn't carry stray spaces.
        noteToSave = '';
      }
      btn.textContent = 'Saving...';
      btn.disabled = true;
      try {
        await API.post('/lead-note-save', { ref: ref, note: noteToSave });
        // Reflect the stamped value back into the UI so the next edit appends
        // a fresh stamp at the bottom (instead of stacking onto a stale base).
        textarea.value = noteToSave;
        textarea.dataset.lastSaved = noteToSave;
        textarea.scrollTop = textarea.scrollHeight;
        btn.textContent = 'Saved!';
        setTimeout(function() { btn.textContent = 'Save Note'; btn.disabled = false; }, 1500);
      } catch (err) {
        btn.textContent = 'Save Note';
        btn.disabled = false;
        alert('Failed: ' + (err.message || 'Unknown error'));
      }
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Activity-log entry add.
       Pulls the selected chip type + textarea content, POSTs to the
       worker, prepends the new entry to the visible list, clears the
       input. Server-side endpoint /note-entry-add appends to
       lead.noteEntries (or wm.noteEntries) + stamps ts + by. */
    async function handleNoteEntryAdd(btn) {
      // Resolve allQuotes via the injected getter (closure over main.js's IIFE state).
      var allQuotes = getAllQuotes ? getAllQuotes() : [];
      var ref = btn.dataset.ref;
      if (!ref) return;
      var card = btn.closest('.rqd-notes-log');
      if (!card) return;
      var input = card.querySelector('.rqd-notes-log__input');
      if (!input) return;
      var text = (input.value || '').trim();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-08) — Follow-up-only save: if no note
         was typed but the follow-up date was CHANGED (armed date differs from the
         deal's current follow-up), auto-stamp a concise audit note so the save goes
         through (server requires text) and the timer updates to the new date. With no
         follow-up change, an empty note still just focuses the input. */
      var _followUpOnlySave = false;
      if (!text) {
        var _fuCl = card.querySelector('.rqd-notes-log__followup');
        var _fuUser = _fuCl && _fuCl.dataset.userArmed === '1';
        var _fuArmed = _fuCl && _fuCl.dataset.armedDate;
        var _fuQ = allQuotes.find(function(x){ return x.reference === ref; });
        var _fuCur = _fuQ ? String((_fuQ.followUp && _fuQ.followUp.dueAt) || _fuQ.holdUntil || '').slice(0, 10) : '';
        if (_fuUser && _fuArmed && _fuArmed !== _fuCur) {
          text = 'Follow-up moved to ' + _fuArmed + '.';
          /* CUSTOM_FOLLOWUPFIX_BY_CLAUDE (AIOPS 1e26be1d9a86, Ryan 2026-09-14) — the rep typed
             NOTHING; this text is our own stamp. Telling the worker so is what lets it fold the
             correction onto the entry they just made instead of adding a second line. It only makes
             the rule ELIGIBLE - the worker still decides from the feed. */
          _followUpOnlySave = true;
        } else {
          input.focus();
          return;
        }
      }
      // Collect all active chip types (multi-select). Empty array → server
      // coerces to ['note'].
      var activeTypes = [];
      card.querySelectorAll('.rqd-notes-log__types .rqd-notes-log__chip--active').forEach(function(c) {
        if (c.dataset.noteType) activeTypes.push(c.dataset.noteType);
      });
      if (activeTypes.length === 0) activeTypes.push('note');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Read armed follow-
         up date from the inline cluster's data-armed-date attribute.
         Follow-up method chips were dropped (speculative metadata
         without downstream automation to back them up) — wm.followUp
         .method field is preserved in the worker for future revival
         but the theme no longer emits it. */
      var cluster = card.querySelector('.rqd-notes-log__followup');
      var followUpAt = cluster ? (cluster.dataset.armedDate || '') : '';
      /* Ryan 2026-05-16: universal follow-up gate now enforced via
         Save button disabled state (see _syncSaveEnabled). The
         pre-submit alert was removed — rep can SEE the button isn't
         available, no need for a post-click yell. Worker-side gate
         still rejects stale-tab submissions defense-in-depth. */
      btn.textContent = 'Saving…';
      btn.disabled = true;
      input.disabled = true;
      try {
        var payload = { ref: ref, types: activeTypes, text: text };
        if (_followUpOnlySave) payload.followUpOnly = true;
        if (followUpAt) payload.followUpAt = followUpAt;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-29) — send the call outcome so
           the worker advances (no-answer) or resets (connected/inbound) the cadence. */
        if (activeTypes.indexOf('call') !== -1) {
          var _ocSel = card.querySelector('.rqd-notes-log__calloutcome');
          if (_ocSel && _ocSel.dataset.finalOutcome) {
            payload.callOutcome = _ocSel.dataset.finalOutcome;
            if (_ocSel.dataset.callVm) payload.callVm = _ocSel.dataset.callVm; // left | none
            /* CUSTOM_CALLDETAIL_BY_CLAUDE (Ryan 2026-09-20) — optional reason, no-VM leaf only.
               Sent only when it is still consistent with the picked leaf: the dataset is cleared
               by _ocReset and by the detail chip's own toggle-off, but gating here too means a
               stale attribute can never ride along on a different outcome. */
            if (_ocSel.dataset.callDetail && payload.callOutcome === 'no-answer' && payload.callVm === 'none') {
              payload.callDetail = _ocSel.dataset.callDetail;
            }
          }
        }
        var res = await API.post('/note-entry-add', payload);
        var entry = res && res.entry ? res.entry : null;
        // Render the new entry at the top of the list — optimistic if the
        // server didn't echo back, server-confirmed otherwise.
        /* CUSTOM_FOLLOWUPFIX_BY_CLAUDE (AIOPS 1e26be1d9a86, Ryan 2026-09-14) - the worker folded
           this correction onto the entry the rep just made, so there is NO new entry to prepend.
           Prepending anyway would paint a line the server never created, and the next refresh
           would silently delete it: the "it saved, then it vanished" shape. */
        var _amended = !!(res && res.amended);
        var list = card.querySelector('.rqd-notes-log__entries');
        var empty = card.querySelector('.rqd-notes-log__empty');
        if (empty) empty.remove();
        if (list && !_amended) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — 'text' was missing
             from these optimistic-render maps, so a just-saved Text entry
             showed as '💬 Note' until the next full feed render (the
             activity-card maps + worker whitelist already knew 'text'). */
          var legacyEmojiMap = { call: '📞', text: '📱', email: '✉', meeting: '🤝', dropin: '🚪', note: '💬', followup: '📅', win: '✅' };
          var legacyLabelMap = { call: 'Call', text: 'Text', email: 'Email', meeting: 'Meeting', dropin: 'Drop-In', note: 'Note', followup: 'Follow-up', win: 'Win' };
          var typesArr = (entry && Array.isArray(entry.types)) ? entry.types : activeTypes;
          var emojiStr = typesArr.map(function(t) { return legacyEmojiMap[t] || legacyEmojiMap.note; }).join('');
          var labelStr = typesArr.map(function(t) { return legacyLabelMap[t] || 'Note'; }).join(' · ');
          var ts = (entry && entry.ts) || new Date().toISOString();
          var by = (entry && entry.by) || ((window.RepPortal && window.RepPortal.Auth.getSession() || {}).code) || '';
          var safeText = (entry && entry.text ? entry.text : text)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>');
          // Just-added entry is always rep-mutable (within 30-min window).
          var entryId = (entry && entry.id) || '';
          var mutateBtns = entryId
            ? '<button type="button" class="rqd-notes-log__entry-edit-btn" data-ref="' + ref + '" data-entry-id="' + entryId + '" title="Edit this note" aria-label="Edit note">&#9998;</button>'
              + '<button type="button" class="rqd-notes-log__entry-delete" data-ref="' + ref + '" data-entry-id="' + entryId + '" title="Delete this note" aria-label="Delete note">&#10005;</button>'
            : '';
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Optimistic
             render includes the follow-up indicator below the note text
             when the save armed a follow-up. Previously this hand-built
             HTML was missing the label — the full render path at
             buildDetailHtml (~L7422) had it, so the label would appear
             on the next row-expand or page refresh, but the immediate
             feedback after Save was just "Call Bill" with no follow-up
             trace. Bug ran for ~3 ship cycles before catching it.
             Server response (entry.followUpAt) takes precedence; falls
             back to the rep's typed followUpAt if the server didn't
             echo it back (defensive). */
          var fuAtForEntry = (entry && entry.followUpAt) || (followUpAt || '');
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Delta-aware
             follow-up label in the optimistic prepend. Server stamps
             entry.previousFollowUpAt on the response when the rep
             edited an existing hold's wake date (ON HOLD edit, or
             changing a pending/wm follow-up that was already set).
             Renders "Follow-up increased/decreased. Was at X, set
             for Y — Date" instead of the plain "set for Xd" in that
             case. */
          var prevFuAtForEntry = (entry && entry.previousFollowUpAt) || '';
          var followupHtml = '';
          if (fuAtForEntry) {
            var _fuLabelOpt;
            if (prevFuAtForEntry) {
              var _oldMsOpt = Date.parse(prevFuAtForEntry + 'T08:00:00Z');
              var _newMsOpt = Date.parse(fuAtForEntry + 'T08:00:00Z');
              var _dirOpt = _newMsOpt > _oldMsOpt ? 'increased' : (_newMsOpt < _oldMsOpt ? 'decreased' : '');
              if (_dirOpt) {
                var _oldStrOpt = formatFollowUpRemaining(ts, prevFuAtForEntry);
                var _newStrOpt = formatFollowUpRemaining(ts, fuAtForEntry);
                _fuLabelOpt = _FU_LABEL + ' ' + _dirOpt + '. Was at ' + _oldStrOpt + ', now set for ' + _newStrOpt + ' - ' + formatFollowUpDate(fuAtForEntry);
              }
            }
            if (!_fuLabelOpt) {
              var _fuDays = _followUpDayCount(ts, fuAtForEntry);
              _fuLabelOpt = _FU_LABEL + ' Set for ' + _fuDays + 'd';
              if (_fuDays > 0) _fuLabelOpt += ' - ' + formatFollowUpDate(fuAtForEntry);
            }
            followupHtml = '<div class="rqd-notes-log__entry-followup" title="Wake date ' + fuAtForEntry + '">' + _fuLabelOpt + '</div>';
          }
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-29) — call-outcome badge.
             Direction derives from callOutcome (inbound→Inbound, else Outbound);
             VM detail from callVm. Built from fixed strings (safe). Source =
             server entry if echoed, else the payload we just sent. */
          /* CUSTOM_FEEDFORMAT_BY_CLAUDE (Ryan 2026-09-12) — ⛔ THIS IS THE SECOND RENDERER AND IT
             HAS TO MATCH THE FIRST. The feed's persisted markup lives in dashboard-activity-card.js;
             this one hand-builds the entry that was just saved so it appears without a refetch. If
             only one of the two is restyled, a call looks one way the instant you save it and
             another way after the next refresh — which is precisely the inconsistency this pass
             was about. Same wording rules: no "no answer" when a voicemail was left, and inbound
             carries no outcome. */
          var _ocBadgeSrc = entry || payload;
          var _callDetails = '';
          if (_ocBadgeSrc && _ocBadgeSrc.callOutcome) {
            var _ocO = String(_ocBadgeSrc.callOutcome);
            if (_ocO === 'inbound') _callDetails = 'Inbound';
            else if (_ocO === 'connected') _callDetails = 'Outbound, connected';
            else if (_ocBadgeSrc.callVm === 'left') _callDetails = 'Outbound, left voicemail';
            else _callDetails = 'Outbound, no answer';
            _callDetails += callDetailTail(_ocBadgeSrc); // CUSTOM_CALLDETAIL_BY_CLAUDE — "… — mailbox full"
          }
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — Call entries (outcome
             pill present): mirror the persisted layout from the start — pill
             heading row, note text on its own row below. Previously the
             optimistic render inlined the text next to the pill and it only
             "dropped to its rightful place" on the next full feed render. */
          var _whenHtml = '<span class="rqd-notes-log__entry-when">' + formatNoteDate(ts) + (by ? ' — ' + by : '') + '</span><span class="rqd-notes-log__entry-mutate-btns">' + mutateBtns + '</span>';
          var entryHtml;
          if (_callDetails) {
            entryHtml = '<div class="rqd-notes-log__entry" data-entry-id="' + entryId + '">'
              + '<div class="rqd-notes-log__entry-meta"><span class="rqd-notes-log__entry-type">' + emojiStr + ' ' + labelStr + '</span>' + _whenHtml + '</div>'
              + '<div class="rqd-notes-log__entry-line"><span class="rqd-notes-log__entry-line-label">Call details:</span> ' + _callDetails + '</div>'
              + (safeText ? '<div class="rqd-notes-log__entry-line"><span class="rqd-notes-log__entry-line-label">Call notes:</span> ' + safeText + '</div>' : '')
              + followupHtml
              + '</div>';
          } else {
            entryHtml = '<div class="rqd-notes-log__entry" data-entry-id="' + entryId + '">'
              + '<div class="rqd-notes-log__entry-meta"><span class="rqd-notes-log__entry-type">' + emojiStr + ' ' + labelStr + '</span><span class="rqd-notes-log__entry-meta-sep"> — </span><span class="rqd-notes-log__entry-text">' + safeText + '</span>' + _whenHtml + '</div>'
              + followupHtml
              + '</div>';
          }
          list.insertAdjacentHTML('afterbegin', entryHtml);
          // Phase 1.33 — Worker may have auto-stamped a transition entry
          // when this note flipped the prospect's stage (cold → engaged
          // on a contact-type note). Prepend it BEFORE the user's note
          // so the chronology reads top-down: transition → note (server
          // unshifts in that order too).
          if (res && res.transitionEntry && res.transitionEntry.transition) {
            var _te = res.transitionEntry;
            var _teFromStage = String(_te.transition.from || '').toLowerCase();
            var _teToStage = String(_te.transition.to || '').toLowerCase();
            var _teFromCls = _teFromStage ? ' rqd-notes-log__transition-pill--prospect-' + _teFromStage : '';
            var _teToCls = _teToStage ? ' rqd-notes-log__transition-pill--prospect-' + _teToStage : '';
            var _teMeta = formatNoteDate(_te.ts || ts) + (_te.by ? ' — ' + _te.by : '');
            var _teHtml = '<div class="rqd-notes-log__entry" data-entry-id="' + (_te.id || '') + '">'
              + '<div class="rqd-notes-log__entry-meta">'
              +   '<span class="rqd-notes-log__entry-transition">'
              +     '<span class="rqd-notes-log__transition-pill' + _teFromCls + '">' + (_te.transition.fromLabel || _teFromStage.toUpperCase()) + '</span>'
              +     '<span class="rqd-notes-log__transition-arrow">→</span>'
              +     '<span class="rqd-notes-log__transition-pill' + _teToCls + '">' + (_te.transition.toLabel || _teToStage.toUpperCase()) + '</span>'
              +   '</span>'
              +   '<span class="rqd-notes-log__entry-mutate-btns"></span>'
              +   '<span class="rqd-notes-log__entry-when">' + _teMeta + '</span>'
              + '</div>'
              + '</div>';
            list.insertAdjacentHTML('afterbegin', _teHtml);
          }
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Bump the count
           badge in the ACTIVITY header so it reflects the new entry
           without waiting for the next full re-render. If the badge
           doesn't exist yet (this was the first entry), inject one. */
        (function _bumpCount() {
          var titleEl = card.querySelector('.rqd-detail__section-title');
          if (!titleEl) return;
          // Phase 1.33 — Bump by 2 when the worker auto-added a
          // transition entry alongside the user's note.
          var _bumpBy = (res && res.transitionEntry) ? 2 : 1;
          var countEl = titleEl.querySelector('.rqd-notes-log__count');
          if (countEl) {
            var n = parseInt(String(countEl.textContent).replace(/\D/g, ''), 10);
            countEl.textContent = '(' + ((isFinite(n) ? n : 0) + _bumpBy) + ')';
          } else {
            var badge = document.createElement('span');
            badge.className = 'rqd-notes-log__count';
            badge.textContent = ' (' + _bumpBy + ')';
            var chev = titleEl.querySelector('.rqd-arap__chevron');
            if (chev) titleEl.insertBefore(badge, chev);
            else titleEl.appendChild(badge);
          }
        })();
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Composer reset
           after save:
             1. Clear text + prefilled-tracker.
             2. Reset type chips to "Note" default.
             3. Clear the armed follow-up cluster + method chips.
             4. If the row STILL has no follow-up (rep saved a note
                without one) AND the lifecycle default applies, re-arm
                the default for the next note. If the row now has a
                follow-up (just saved one), leave the cluster cleared
                so the rep isn't pushed to set another. */
        input.value = '';
        /* CUSTOM_COMPOSERHEIGHT_BY_CLAUDE (Ryan 2026-09-20) — `''`, not `'auto'`. This was the
           partner of the auto-grow handler that used to run on every keystroke; with that gone
           the field is sized by `height: 100%` of the grid row, and ANY inline height — `auto`
           included — overrides it. Both land on 204px today only because the box is empty here
           and the 204px floor catches it, which is a coincidence rather than a reason.
           ⛔ This is now the last place that touched the field's height, so "nothing writes an
           inline height on .rqd-notes-log__input" is a rule, and tests/composer-height-tests.mjs
           asserts it. */
        input.style.height = '';
        delete input.dataset.prefilled;
        var chips = card.querySelectorAll('.rqd-notes-log__types .rqd-notes-log__chip');
        chips.forEach(function(c) {
          var isDefault = c.dataset.noteType === 'note';
          c.classList.toggle('rqd-notes-log__chip--active', isDefault);
          c.setAttribute('aria-pressed', isDefault ? 'true' : 'false');
        });
        /* Focus mode reset (Ryan 2026-05-29): drop .is-calling so the type chips
           re-expand, and clear any picked call outcome. */
        var _typesEl = card.querySelector('.rqd-notes-log__types');
        if (_typesEl) _typesEl.classList.remove('is-calling');
        var _ocEl = card.querySelector('.rqd-notes-log__calloutcome');
        if (_ocEl) {
          _ocEl.dataset.ocStage = 'root';
          delete _ocEl.dataset.finalOutcome;
          _ocEl.querySelectorAll('.rqd-notes-log__oc-step').forEach(function (s) { s.hidden = (s.dataset.ocStep !== 'root'); });
          var _crEl = _ocEl.querySelector('.rqd-notes-log__oc-crumbs');
          if (_crEl) _crEl.innerHTML = '<span class="rqd-notes-log__oc-crumb-label">Outcome</span>';
        }
        clearFollowUp(card);
        // Update local q.noteEntries + q.followUp so a re-render survives.
        var q = allQuotes.find(function(x) { return x.reference === ref; });
        if (q) {
          if (!Array.isArray(q.noteEntries)) q.noteEntries = [];
          /* CUSTOM_FOLLOWUPFIX_BY_CLAUDE - REPLACE, DO NOT PREPEND, when the worker amended. The
             returned entry carries the id it already had on the feed; unshifting it would leave
             the row holding the same entry twice, once with each date. */
          if (entry && _amended) {
            var _ai = q.noteEntries.findIndex(function (e) { return e && e.id === entry.id; });
            if (_ai >= 0) q.noteEntries[_ai] = entry; else q.noteEntries.unshift(entry);
            /* Redraw just the entries list so the corrected date shows on the entry carrying it.
               The composer is a SIBLING subtree, so nothing typed is at risk - see the note on
               _repaintEntriesFor in dashboard-main.js. */
            if (typeof repaintEntriesFor === 'function') repaintEntriesFor(ref);
          } else if (entry) {
            q.noteEntries.unshift(entry);
          }
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE QUEUED EMAIL DISAPPEARS ON THIS
             CLICK. Logging a call, a text or a meeting cancels a live sequence server-side, and
             `/note-entry-add` now waits for that and reports it back as `automation` (see the
             header on _automationStoppedField in handlers/lead-routes.js). Without this the row
             kept its stale `pendingAutomation` and `buildSequenceBlock` went on painting an email
             that had already been cancelled until the two-minute poll or a manual refresh.
             ⚠️ BOTH HALVES ARE NEEDED. Clearing the DOM alone would let the next re-render (a
             row collapse, a filter change) bring the block straight back off the stale row; and
             clearing the row alone would not repaint the card that is already open in front of
             the rep. The quiet refetch below is what makes it durable rather than optimistic.
             📌 ABSENT MEANS NOTHING WAS CANCELLED — the field is omitted, never sent false, so a
             plain note on a deal with a running sequence leaves the block exactly where it is. */
          if (res && res.automation && res.automation.stopped) {
            q.pendingAutomation = null;
            /* ⚠️ THE WORDING COMES FROM THE WORKER (`automation.text`), which builds it with the
               same helper the deal-feed entry uses — so the block and the entry that lands
               beside it cannot describe the same stop two ways. */
            var _stopWhy = res.automation.text || 'Automated emails stopped on this deal.';
            if (window.RQD && typeof window.RQD.clearSequenceBlock === 'function') {
              window.RQD.clearSequenceBlock(ref, _stopWhy);
            }
            try { fetchQuotes({ quiet: true }); } catch (_) { /* the 2-minute poll is the backstop */ }
          }
          /* CUSTOM_CALLWAIT_BY_CLAUDE (AIOPS 5f9a062bf0da) — the call this deal was parked on was
             just logged and the worker woke the agent: drop the "parked" block on this click, the
             same shape as the sequence block above. Absent means nothing was parked. */
          if (res && res.callWait && res.callWait.woken) {
            q.callWait = { status: 'called', nextExpectedLane: res.callWait.nextExpectedLane || null, calledAt: new Date().toISOString() };
            if (window.RQD && typeof window.RQD.clearCallWaitBlock === 'function') {
              window.RQD.clearCallWaitBlock(ref, res.callWait.text || 'Call logged. The AI agent was woken to continue.');
            }
            try { fetchQuotes({ quiet: true }); } catch (_) { /* the 2-minute poll is the backstop */ }
          }
          if (followUpAt) {
            // Surface immediately on the row's To-Do badge before next fetch.
            q.followUp = { dueAt: followUpAt, setBy: ((window.RepPortal && window.RepPortal.Auth.getSession() || {}).code) || '', setAt: new Date().toISOString(), reason: text };
            // If this is a lead-state row, the server auto-held it. Re-fetch to
            // pick up the status change so the row paints the ON HOLD pill.
            if (q.isLead || q.isSample || q.isDeadLead || q.isOnHold) {
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Optimistic
                 hold-countdown update. If the row is already ON HOLD,
                 the existing .rqd-hold-countdown badge in the To-Do
                 column has data-hold-until="<old date>" — the next
                 live-tick won't reflect the new wake date until
                 fetchQuotes() finishes (~500ms-1s lag). Patch the DOM
                 attribute + local q.holdUntil immediately so the
                 countdown jumps to the new value within 1s instead of
                 waiting for the round-trip. */
              q.holdUntil = followUpAt;
              var _holdBadge = document.querySelector(
                'tr[data-ref="' + ref.replace(/"/g, '\\"') + '"] .rqd-hold-countdown[data-hold-until]'
              );
              if (_holdBadge) _holdBadge.dataset.holdUntil = followUpAt;
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-23) — instant 🔔 clear.
                 Logging a follow-up on a lead-state row auto-holds it server-side
                 (the row becomes ON HOLD → ⏳ countdown, no bell). The quiet
                 fetchQuotes below is the authoritative repaint, but it re-pulls
                 and re-renders the WHOLE board (worker ~3-4s to assemble + multi-MB
                 payload), so the bell used to linger many seconds after the save.
                 Mirror the server auto-hold locally (isOnHold; holdUntil already
                 set above) and re-render just this row's To-Do cell so the bell
                 disappears immediately. The background fetch still reconciles the
                 ON HOLD pill + filter counts. */
              q.isOnHold = true;
              if (typeof refreshProgressBar === 'function') refreshProgressBar(ref);
              /* Ryan 2026-05-21: quiet refresh so the row stays expanded
                 and the page doesn't scroll-jump to the top after the
                 auto-hold transition repaints the row's status pill +
                 To-Do cell. */
              fetchQuotes({ quiet: true });
            } else {
              // Quote-state — just refresh the To Do cell on this row.
              if (typeof refreshProgressBar === 'function') refreshProgressBar(ref);
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — a prospect follow-up armed via
                 the composer also changes the 🔔 Follow Up count badge on the filter toggle;
                 refresh the prospect filter bar (no-op / absent for offline deals). */
              if (q.cfaCode && q.prospectStage && window.RQD && typeof window.RQD.syncProspectFilters === 'function') {
                window.RQD.syncProspectFilters();
              }
            }
          }
        }
        // Re-arm default follow-up for the NEXT note if the row still
        // has no follow-up/hold set AND the lifecycle wants one. Uses
        // the data-default-days attribute baked at render time so the
        // logic doesn't have to re-derive lifecycle state here.
        var nextDefault = parseInt(card.dataset.defaultDays || '0', 10);
        var stillHasNoFollowUp = !(q && ((q.followUp && q.followUp.dueAt) || q.holdUntil));
        if (nextDefault > 0 && stillHasNoFollowUp) {
          armFollowUp(card, daysFromNowYmd(nextDefault), nextDefault);
        }
      } catch (err) {
        alert('Note save failed: ' + (err && err.message ? err.message : 'Unknown error'));
      } finally {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Restore the
           Save button to its correct label (Save or Put on Hold) based
           on the cluster's current armed state. After-save reset above
           may have re-armed the lifecycle default (lead-state rows with
           still-no-follow-up); that path called armFollowUp which sets
           the right label too. This finally branch covers error paths
           and the non-re-armed completion path so the button never
           sticks on "Saving…". */
        input.disabled = false;
        var _btnCluster = card.querySelector('.rqd-notes-log__followup');
        var _btnArmed = !!(_btnCluster && _btnCluster.dataset.armedDate);
        if (btn.dataset.holdable === '1') {
          btn.textContent = _btnArmed ? (btn.dataset.holdLabel || 'Put on Hold') : (btn.dataset.saveLabel || 'Save');
        } else {
          btn.textContent = btn.dataset.saveLabel || 'Save';
        }
        /* After save: textarea is now empty (cleared in the success
           path) so disable the button until the rep types again. Error
           paths keep the rep's text in the input so the button stays
           enabled. */
        btn.disabled = !(input.value && input.value.trim());
        input.focus();
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Activity-log entry
       edit. Click ✎ swaps the entry's text div for a textarea + Save/
       Cancel buttons (in-place edit). Save posts to /note-entry-edit;
       on success the entry switches back to view mode with updated
       text + (edited) badge. Cancel discards changes. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Mobile-only
       Edit/Delete popover anchored to a tapped activity-log timestamp.
       The desktop ✎ + ✕ icons are CSS-hidden on mobile (cramped + easy
       to mis-tap); tap-on-timestamp surfaces them in a small popup
       instead. Reuses the existing edit/delete buttons (still in the
       DOM, just visually hidden) so the handlers don't need to change.
       Dismisses on outside-tap or after either action fires. */
    function showEntryMutatePopover(anchorEl, editBtn, delBtn) {
      // Strip any existing popover (re-tap on same/different entry).
      document.querySelectorAll('.rqd-entry-mutate-popover').forEach(function(el) { el.remove(); });
      var pop = document.createElement('div');
      pop.className = 'rqd-entry-mutate-popover';
      if (editBtn) {
        var editLink = document.createElement('button');
        editLink.type = 'button';
        editLink.className = 'rqd-entry-mutate-popover__btn rqd-entry-mutate-popover__btn--edit';
        editLink.textContent = '✎ Edit';
        editLink.addEventListener('click', function() {
          pop.remove();
          enterNoteEditMode(editBtn);
        });
        pop.appendChild(editLink);
      }
      if (delBtn) {
        var delLink = document.createElement('button');
        delLink.type = 'button';
        delLink.className = 'rqd-entry-mutate-popover__btn rqd-entry-mutate-popover__btn--delete';
        delLink.textContent = '✕ Delete';
        delLink.addEventListener('click', function() {
          pop.remove();
          handleNoteEntryDelete(delBtn);
        });
        pop.appendChild(delLink);
      }
      // Position: fixed, anchored to the timestamp's bottom-left corner
      // with a small offset, clamped to the viewport's right edge so it
      // doesn't overflow on narrow screens.
      document.body.appendChild(pop);
      var rect = anchorEl.getBoundingClientRect();
      var popRect = pop.getBoundingClientRect();
      var vw = window.innerWidth;
      var leftPx = Math.min(rect.left, vw - popRect.width - 8);
      pop.style.position = 'fixed';
      pop.style.left = Math.max(8, leftPx) + 'px';
      pop.style.top = (rect.bottom + 4) + 'px';
      pop.style.zIndex = '10000';
      // Dismiss on outside tap (next-tick so the originating click doesn't
      // immediately close it). Use {once: true} to keep the listener clean.
      setTimeout(function() {
        function dismiss(ev) {
          if (!pop.contains(ev.target)) {
            pop.remove();
            document.removeEventListener('click', dismiss, true);
          }
        }
        document.addEventListener('click', dismiss, true);
      }, 0);
    }
    function enterNoteEditMode(btn) {
      var entryEl = btn.closest('.rqd-notes-log__entry');
      if (!entryEl || entryEl.classList.contains('rqd-notes-log__entry--editing')) return;
      var textEl = entryEl.querySelector('.rqd-notes-log__entry-text');
      if (!textEl) return;
      var ref = btn.dataset.ref;
      var entryId = btn.dataset.entryId;
      // Stash the original HTML for Cancel restore.
      var originalHtml = textEl.innerHTML;
      entryEl.dataset.originalTextHtml = originalHtml;
      // Recover plain text from the rendered HTML (convert <br> back to \n,
      // unescape HTML entities via a temp element).
      var temp = document.createElement('div');
      temp.innerHTML = originalHtml.replace(/<br\s*\/?>/gi, '\n');
      var plainText = temp.textContent || temp.innerText || '';
      entryEl.classList.add('rqd-notes-log__entry--editing');
      var formHtml = '<div class="rqd-notes-log__entry-edit-form">'
        + '<textarea class="rqd-notes-log__entry-edit-input" data-ref="' + esc(ref) + '" data-entry-id="' + esc(entryId) + '">' + esc(plainText) + '</textarea>'
        + '<div class="rqd-notes-log__entry-edit-actions">'
        +   '<button type="button" class="rqd-notes-log__entry-edit-save" data-ref="' + esc(ref) + '" data-entry-id="' + esc(entryId) + '">Save</button>'
        +   '<button type="button" class="rqd-notes-log__entry-edit-cancel">Cancel</button>'
        + '</div>'
        + '</div>';
      textEl.style.display = 'none';
      textEl.insertAdjacentHTML('afterend', formHtml);
      var newTa = entryEl.querySelector('.rqd-notes-log__entry-edit-input');
      if (newTa) {
        newTa.focus();
        // Place caret at end.
        var len = newTa.value.length;
        try { newTa.setSelectionRange(len, len); } catch (_) {}
      }
    }
    function exitNoteEditMode(entryEl) {
      if (!entryEl) return;
      entryEl.classList.remove('rqd-notes-log__entry--editing');
      var form = entryEl.querySelector('.rqd-notes-log__entry-edit-form');
      if (form) form.remove();
      var textEl = entryEl.querySelector('.rqd-notes-log__entry-text');
      if (textEl) textEl.style.display = '';
      delete entryEl.dataset.originalTextHtml;
    }
    async function handleNoteEntryEdit(btn) {
      // Resolve allQuotes via the injected getter (closure over main.js's IIFE state).
      var allQuotes = getAllQuotes ? getAllQuotes() : [];
      var ref = btn.dataset.ref;
      var entryId = btn.dataset.entryId;
      if (!ref || !entryId) return;
      var entryEl = btn.closest('.rqd-notes-log__entry');
      if (!entryEl) return;
      var ta = entryEl.querySelector('.rqd-notes-log__entry-edit-input');
      if (!ta) return;
      var text = (ta.value || '').trim();
      if (!text) {
        ta.focus();
        return;
      }
      btn.textContent = 'Saving…';
      btn.disabled = true;
      ta.disabled = true;
      try {
        var res = await API.post('/note-entry-edit', { ref: ref, entryId: entryId, text: text });
        var entry = res && res.entry ? res.entry : null;
        // Update the text div with the new content + add/refresh (edited) badge.
        var textEl = entryEl.querySelector('.rqd-notes-log__entry-text');
        var safeText = (entry && entry.text ? entry.text : text)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        if (textEl) textEl.innerHTML = safeText;
        // Refresh the (edited) badge in the meta row.
        var whenEl = entryEl.querySelector('.rqd-notes-log__entry-when');
        if (whenEl && entry && entry.editedAt) {
          var existingBadge = whenEl.querySelector('.rqd-notes-log__entry-edited');
          var badgeTitle = 'Edited ' + formatNoteDate(entry.editedAt) + (entry.editedBy ? ' by ' + entry.editedBy : '');
          if (existingBadge) {
            existingBadge.title = badgeTitle;
          } else {
            // Insert badge BEFORE the mutate buttons (so it sits next to the timestamp text).
            var firstBtn = whenEl.querySelector('.rqd-notes-log__entry-edit-btn, .rqd-notes-log__entry-delete');
            var badgeHtml = ' <span class="rqd-notes-log__entry-edited" title="' + esc(badgeTitle) + '">(edited)</span>';
            if (firstBtn) {
              firstBtn.insertAdjacentHTML('beforebegin', badgeHtml);
            } else {
              whenEl.insertAdjacentHTML('beforeend', badgeHtml);
            }
          }
        }
        // Sync local cache.
        var q = allQuotes.find(function(x) { return x.reference === ref; });
        if (q && Array.isArray(q.noteEntries) && entry) {
          for (var i = 0; i < q.noteEntries.length; i++) {
            if (q.noteEntries[i] && q.noteEntries[i].id === entryId) {
              q.noteEntries[i] = entry;
              break;
            }
          }
        }
        exitNoteEditMode(entryEl);
      } catch (err) {
        btn.textContent = 'Save';
        btn.disabled = false;
        ta.disabled = false;
        alert('Edit failed: ' + (err && err.message ? err.message : 'Unknown error'));
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Activity-log entry
       delete. Optimistic UI: remove the entry's DOM node immediately on
       confirm + restore it if the server 403s (out-of-window for reps).
       Updates local q.noteEntries so re-renders survive. */
    async function handleNoteEntryDelete(btn) {
      // Resolve allQuotes via the injected getter (closure over main.js's IIFE state).
      var allQuotes = getAllQuotes ? getAllQuotes() : [];
      var ref = btn.dataset.ref;
      var entryId = btn.dataset.entryId;
      if (!ref || !entryId) return;
      if (!confirm('Delete this note?')) return;
      var entryEl = btn.closest('.rqd-notes-log__entry');
      var card = btn.closest('.rqd-notes-log');
      // Optimistic removal — stash the node + its place in DOM for restore on error.
      var prevSibling = entryEl ? entryEl.previousElementSibling : null;
      var parentList = entryEl ? entryEl.parentElement : null;
      if (entryEl) entryEl.remove();
      try {
        await API.post('/note-entry-delete', { ref: ref, entryId: entryId });
        // Sync local cache.
        var q = allQuotes.find(function(x) { return x.reference === ref; });
        if (q && Array.isArray(q.noteEntries)) {
          q.noteEntries = q.noteEntries.filter(function(x) { return x && x.id !== entryId; });
        }
        // If the list is now empty, render the placeholder.
        if (parentList && !parentList.querySelector('.rqd-notes-log__entry')) {
          parentList.innerHTML = '<div class="rqd-notes-log__empty">No activity yet. Add the first note above.</div>';
        }
        /* Ryan 2026-05-16: decrement the count badge in the ACTIVITY
           header. Remove the badge entirely when it hits 0 (matches
           the render-time "skip when zero" rule). */
        (function _decCount() {
          var titleEl = card && card.querySelector('.rqd-detail__section-title');
          if (!titleEl) return;
          var countEl = titleEl.querySelector('.rqd-notes-log__count');
          if (!countEl) return;
          var n = parseInt(String(countEl.textContent).replace(/\D/g, ''), 10);
          var next = (isFinite(n) ? n : 1) - 1;
          if (next <= 0) countEl.remove();
          else countEl.textContent = '(' + next + ')';
        })();
      } catch (err) {
        // Restore DOM on failure
        if (entryEl && parentList) {
          if (prevSibling && prevSibling.parentElement === parentList) {
            prevSibling.insertAdjacentElement('afterend', entryEl);
          } else {
            parentList.prepend(entryEl);
          }
        }
        alert('Delete failed: ' + (err && err.message ? err.message : 'Unknown error'));
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // ── Delete Pending ──


  // === Public API ===
  window.RQD.handlePendingNoteSave = handlePendingNoteSave;
  window.RQD.handleLeadNoteSave = handleLeadNoteSave;
  window.RQD.handleNoteEntryAdd = handleNoteEntryAdd;
  window.RQD.showEntryMutatePopover = showEntryMutatePopover;
  window.RQD.enterNoteEditMode = enterNoteEditMode;
  window.RQD.exitNoteEditMode = exitNoteEditMode;
  window.RQD.handleNoteEntryEdit = handleNoteEntryEdit;
  window.RQD.handleNoteEntryDelete = handleNoteEntryDelete;
  // formatLeadNoteStamp stays internal (only called by handleLeadNoteSave)

  // === Init — main.js calls this once at boot ===
  window.RQD.initDealFeed = function(d) {
    d = d || {};
    esc = d.esc;
    formatNoteDate = d.formatNoteDate;
    formatFollowUpDate = d.formatFollowUpDate;
    daysFromNowYmd = d.daysFromNowYmd;
    fetchQuotes = d.fetchQuotes;
    getAllQuotes = d.getAllQuotes;
    armFollowUp = d.armFollowUp;
    clearFollowUp = d.clearFollowUp;
    _syncSaveEnabled = d._syncSaveEnabled;
    formatFollowUpRemaining = d.formatFollowUpRemaining;
    _followUpDayCount = d._followUpDayCount;
    refreshProgressBar = d.refreshProgressBar;
    repaintEntriesFor = d.repaintEntriesFor;   // CUSTOM_FOLLOWUPFIX_BY_CLAUDE
    API = d.API || (window.RepPortal && window.RepPortal.API);
    Auth = d.Auth || (window.RepPortal && window.RepPortal.Auth);
  };
})();
